<!--
  ConfirmEmailChangePage -- redeems an email-change confirm token.

  Card: "Personal Profile page" -- https://trello.com/c/BxskXBad
  Route: /confirm-email/:token  (PUBLIC -- no session required)
  Backend: POST /api/me/email/confirm/<token>

  Why public: the confirm link is delivered ONLY to the new address and
  is followed from that mailbox -- routinely a different browser, or a
  different device, from the one holding the session. Requiring a login
  here would make the flow fail precisely when it is working as designed.
  Authorization comes from possession of the single-use, expiring,
  unguessable token itself, the same trust model as the existing
  password-reset link.

  The page redeems on mount and reports one of exactly two outcomes --
  applied, or not applied with the reason. It never reports a partial or
  ambiguous state, because the backend either moves the address or does
  not.

  ASCII-only.
-->
<template>
    <div class="confirm-email" data-testid="confirm-email-page">
        <p v-if="working" data-testid="confirm-email-working">
            Confirming your new email address...
        </p>

        <template v-else-if="confirmedEmail">
            <h1 class="confirm-email__title">Email address updated</h1>
            <p data-testid="confirm-email-success">
                Your account email is now {{ confirmedEmail }}. You can sign in
                with it from now on.
            </p>
            <router-link to="/login" data-testid="confirm-email-login-link">
                Go to sign in
            </router-link>
        </template>

        <template v-else>
            <h1 class="confirm-email__title">We could not confirm that link</h1>
            <p data-testid="confirm-email-error">{{ error }}</p>
            <p class="confirm-email__hint">
                Confirmation links expire and can only be used once. Request
                the change again from your Profile page to get a fresh link.
            </p>
        </template>
    </div>
</template>

<script>
import { confirmEmailChange } from "@/services/profileService";

export default {
    name: "ConfirmEmailChangePage",
    data() {
        return {
            working: true,
            confirmedEmail: "",
            error: "",
        };
    },
    async mounted() {
        const token = this.$route.params.token;
        if (!token) {
            this.working = false;
            this.error = "That confirmation link is not valid.";
            return;
        }
        try {
            const data = await confirmEmailChange(token);
            this.confirmedEmail = data.email || "";
            if (!this.confirmedEmail) {
                // A 200 with no address would mean the contract changed
                // under us; do not claim success we cannot evidence.
                this.error =
                    "We could not confirm that link. Please request the change again.";
            }
        } catch (err) {
            this.error =
                err?.response?.data?.error ||
                "This confirmation link is invalid or has expired.";
        } finally {
            this.working = false;
        }
    },
};
</script>

<style scoped>
.confirm-email {
    max-width: 480px;
    margin: 64px auto;
    padding: 24px;
    text-align: center;
}

.confirm-email__title {
    font-size: 1.35rem;
    font-weight: 600;
    margin: 0 0 12px;
}

.confirm-email__hint {
    font-size: 0.8125rem;
    color: var(--tf-color-text-muted, #666);
    margin-top: 16px;
}
</style>
